import React, { useState, useMemo } from 'react'
import {
  Box,
  Alert,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CategoryIcon from '@mui/icons-material/Category'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import {
  AutoCoderFramework,
  AutoCoderVariable,
  FrameworkEngineConfig,
  CarbonInterviewRawDataRow,
  SourceVariable,
} from '../../../types/gotypes'
import useFrameworkLibrary, { AutoCoderFrameworkLibrary, FrameworkData } from '../../../hooks/useFrameworkLibrary'
import { buildColorMap } from '../colorPalette'
import ManualFrameworkEditor from '../engine/ManualFrameworkEditor'
import CodeCard from './CodeCard'
import CodeFramePicker from './CodeFramePicker'
import ThemeAIModal from './ThemeAIModal'
import useAccount from '../../../hooks/useAccount'

interface FrameworkComposerProps {
  frameworks: AutoCoderFramework[]
  frameworkLibrary: AutoCoderFrameworkLibrary[]
  frameworkLibraryLoading: boolean
  onFrameworksChange: (frameworks: AutoCoderFramework[]) => void
  disabled?: boolean
  // ThemeAI integration
  frameworkEngine?: FrameworkEngineConfig
  onFrameworkEngineChange?: (config: FrameworkEngineConfig) => void
  hasDataConfirmed: boolean
  previewData: CarbonInterviewRawDataRow[]
  variable?: SourceVariable
  customer: string
  job: string
  pipelineId: string
}

const FrameworkComposer: React.FC<FrameworkComposerProps> = ({
  frameworks,
  frameworkLibrary,
  frameworkLibraryLoading,
  onFrameworksChange,
  disabled,
  frameworkEngine,
  onFrameworkEngineChange,
  hasDataConfirmed,
  previewData,
  variable,
  customer,
  job,
  pipelineId,
}) => {
  // Framework library hook for save-to-library from ThemeAI
  const { createFramework, createLoading, setFrameworkVisibility } = useFrameworkLibrary({ customer, job })
  const account = useAccount()
  const currentUserEmail = account.user?.session_info?.email || account.user?.session_info?.name || ''
  const canPromote = Boolean(account.user?.role_permissions?.isManager)

  // UI state
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerMode, setPickerMode] = useState<'main' | 'looped'>('main')
  const [loopedTargetIndex, setLoopedTargetIndex] = useState<number>(-1)
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({})
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [showThemeAI, setShowThemeAI] = useState(false)
  const [loopedPickerOpen, setLoopedPickerOpen] = useState(false)

  // Build color maps for each framework's main variable codes
  const frameworkColorMaps = useMemo(() => {
    const maps: Record<number, Record<string, string>> = {}
    const fws: AutoCoderFramework[] = frameworks || []
    fws.forEach((fw, index) => {
      const codeNames = (fw.main_variable?.codes || []).map((c) => c.text || c.code)
      if (codeNames.length > 0) {
        maps[index] = buildColorMap(codeNames)
      }
    })
    return maps
  }, [frameworks])

  // --- Add Main Variable handlers ---
  const handleAddMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAddMenuAnchor(event.currentTarget)
  }

  const handleAddMenuClose = () => {
    setAddMenuAnchor(null)
  }

  const handleAddFromLibrary = () => {
    handleAddMenuClose()
    setPickerMode('main')
    setPickerOpen(true)
  }

  const handleAddFromThemeAI = () => {
    handleAddMenuClose()
    setShowThemeAI(true)
  }

  const handleAddManual = () => {
    handleAddMenuClose()
    const newFramework: AutoCoderFramework = {
      name: '',
      main_variable: { name: 'topic', codes: [] },
      suggestions: [],
      looped_variables: [],
    }
    onFrameworksChange([...frameworks, newFramework])
    setEditingIndex(frameworks.length)
  }

  // Save ThemeAI themes to library
  const handleSaveToLibrary = async (payload: { name: string; description?: string; data: FrameworkData }) => {
    await createFramework(payload)
  }

  // ThemeAI modal apply handler
  const handleThemeAIApply = (themeFramework: AutoCoderFramework) => {
    const themeFrameworkIndex = frameworks.findIndex(f => f.name === 'theme')
    if (themeFrameworkIndex >= 0) {
      const updated = [...frameworks]
      updated[themeFrameworkIndex] = themeFramework
      onFrameworksChange(updated)
    } else {
      onFrameworksChange([themeFramework, ...frameworks])
    }
    setShowThemeAI(false)
  }

  // Pick from library as main variable
  const handleLibrarySelect = (fw: AutoCoderFrameworkLibrary) => {
    if (!fw.data) return
    const newFramework: AutoCoderFramework = {
      name: fw.data.name,
      main_variable: fw.data.main_variable,
      suggestions: fw.data.suggestions || [],
      looped_variables: [],
    }
    onFrameworksChange([...frameworks, newFramework])
  }

  // Pick from library as looped variable
  const handleLoopedSelect = (fw: AutoCoderFrameworkLibrary) => {
    if (!fw.data || loopedTargetIndex < 0) return
    const updated = [...frameworks]
    const target = { ...updated[loopedTargetIndex] }
    const newLooped: AutoCoderVariable = {
      name: fw.data.main_variable.name,
      codes: fw.data.main_variable.codes,
    }
    target.looped_variables = [...target.looped_variables, newLooped]
    updated[loopedTargetIndex] = target
    onFrameworksChange(updated)
  }

  // Remove framework
  const handleRemoveFramework = (index: number) => {
    const updated = frameworks.filter((_, i) => i !== index)
    onFrameworksChange(updated)
    if (editingIndex === index) setEditingIndex(null)
  }

  // Remove looped variable
  const handleRemoveLooped = (frameworkIndex: number, loopedIndex: number) => {
    const updated = [...frameworks]
    const target = { ...updated[frameworkIndex] }
    target.looped_variables = target.looped_variables.filter((_, i) => i !== loopedIndex)
    updated[frameworkIndex] = target
    onFrameworksChange(updated)
  }

  // Add looped variable from library
  const handleAddLooped = (frameworkIndex: number) => {
    setLoopedTargetIndex(frameworkIndex)
    setPickerMode('looped')
    setLoopedPickerOpen(true)
  }

  // Toggle expand
  const toggleExpand = (index: number) => {
    setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }))
  }

  // Get all used main variable names (for exclude)
  const usedNames = frameworks.map(f => f.name).filter(Boolean)

  // Get existing looped variables for theme framework (to preserve when re-applying)
  const existingThemeLooped = useMemo(() => {
    const themeFramework = frameworks.find(f => f.name === 'theme')
    return themeFramework?.looped_variables
  }, [frameworks])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Add Main Variable button */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          color="info"
          startIcon={<AddIcon />}
          onClick={handleAddMenuOpen}
          disabled={disabled}
        >
          Add Main Variable
        </Button>
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={handleAddMenuClose}
        >
          <MenuItem onClick={handleAddFromLibrary}>
            <CategoryIcon sx={{ mr: 1, fontSize: 20 }} />
            From Library
          </MenuItem>
          <MenuItem onClick={handleAddFromThemeAI}>
            <AutoAwesomeIcon sx={{ mr: 1, fontSize: 20 }} />
            From ThemeAI
          </MenuItem>
          <MenuItem onClick={handleAddManual}>
            <EditIcon sx={{ mr: 1, fontSize: 20 }} />
            Manual
          </MenuItem>
        </Menu>
      </Box>

      {/* Framework cards */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {frameworks.length === 0 && !showThemeAI && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No main variables configured. Click "Add Main Variable" to get started.
          </Alert>
        )}

        {frameworks.map((framework, index) => {
          const isExpanded = expandedCards[index] ?? false
          const isEditing = editingIndex === index
          const codeCount = framework.main_variable?.codes?.length || 0
          const loopedCount = framework.looped_variables?.length || 0
          const colorMap = frameworkColorMaps[index] || {}

          return (
            <Paper
              key={index}
              variant="outlined"
              sx={{ mb: 2, overflow: 'hidden' }}
            >
              {/* Card header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => toggleExpand(index)}
              >
                <IconButton
                  size="small"
                  sx={{ mr: 1 }}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${framework.name || 'framework'}`}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" fontWeight={500} noWrap>
                      {framework.name || '(Unnamed)'}
                    </Typography>
                    <Chip label={`${codeCount} codes`} size="small" variant="outlined" />
                    {loopedCount > 0 && (
                      <Chip label={`${loopedCount} looped`} size="small" color="info" variant="outlined" />
                    )}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    size="small"
                    onClick={() => setEditingIndex(isEditing ? null : index)}
                    disabled={disabled}
                    aria-label={`Edit ${framework.name || 'framework'}`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFramework(index)}
                    disabled={disabled}
                    color="error"
                    aria-label={`Remove ${framework.name || 'framework'}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>

              {/* Looped variables summary (always visible) */}
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">
                    Looped:
                  </Typography>
                  {framework.looped_variables?.length > 0 ? (
                    framework.looped_variables.map((lv, lvIndex) => (
                      <Chip
                        key={lvIndex}
                        label={`${lv.name} (${lv.codes?.length || 0})`}
                        size="small"
                        variant="outlined"
                        onDelete={disabled ? undefined : () => handleRemoveLooped(index, lvIndex)}
                      />
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">(none)</Typography>
                  )}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddLooped(index)}
                    disabled={disabled}
                    sx={{ ml: 1 }}
                  >
                    Add Looped Variable
                  </Button>
                </Stack>
              </Box>

              {/* Expanded content: code details */}
              <Collapse in={isExpanded}>
                <Box sx={{ px: 2, pb: 2, borderTop: 1, borderColor: 'divider', pt: 1 }}>
                  {isEditing ? (
                    <ManualFrameworkEditor
                      frameworks={[framework]}
                      onChange={(updated) => {
                        const all = [...frameworks]
                        all[index] = updated[0]
                        onFrameworksChange(all)
                      }}
                      disabled={disabled}
                      showLoopedVariables
                    />
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Main Variable: {framework.main_variable?.name}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                        {framework.main_variable?.codes?.map((code, ci) => {
                          const codeLabel = code.text || code.code
                          return (
                            <CodeCard
                              key={ci}
                              code={code}
                              color={colorMap[codeLabel]}
                              compact
                            />
                          )
                        })}
                      </Box>

                      {/* Looped variables detail */}
                      {framework.looped_variables?.length > 0 && (
                        <>
                          <Divider sx={{ my: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Looped Variables
                            </Typography>
                          </Divider>
                          {framework.looped_variables.map((lv, lvIndex) => (
                            <Box key={lvIndex} sx={{ mb: 1.5 }}>
                              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}
                                >
                                  {lv.name}
                                </Typography>
                                {!disabled && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveLooped(index, lvIndex)}
                                    sx={{ p: 0.25 }}
                                    aria-label={`Remove looped variable ${lv.name || ''}`}
                                  >
                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                )}
                              </Stack>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {lv.codes?.map((code, ci) => (
                                  <Chip
                                    key={ci}
                                    label={code.text || code.code}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderLeft: '3px solid',
                                      borderLeftColor: 'info.main',
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </Box>
              </Collapse>
            </Paper>
          )
        })}
      </Box>

      {/* ThemeAI Modal */}
      <ThemeAIModal
        open={showThemeAI}
        onCancel={() => setShowThemeAI(false)}
        onApply={handleThemeAIApply}
        onSaveToLibrary={handleSaveToLibrary}
        saveToLibraryLoading={createLoading}
        frameworkEngine={frameworkEngine}
        onFrameworkEngineChange={onFrameworkEngineChange}
        hasDataConfirmed={hasDataConfirmed}
        previewData={previewData}
        variable={variable}
        customer={customer}
        job={job}
        pipelineId={pipelineId}
        disabled={disabled}
        existingLoopedVariables={existingThemeLooped}
      />

      {/* Code Frame Picker for main variables */}
      <CodeFramePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleLibrarySelect}
        library={frameworkLibrary}
        loading={frameworkLibraryLoading}
        title="Select Main Variable from Library"
        excludeNames={usedNames}
        currentUserEmail={currentUserEmail}
        canPromote={canPromote}
        onSetVisibility={setFrameworkVisibility}
      />

      {/* Code Frame Picker for looped variables */}
      <CodeFramePicker
        open={loopedPickerOpen}
        onClose={() => setLoopedPickerOpen(false)}
        onSelect={handleLoopedSelect}
        library={frameworkLibrary}
        loading={frameworkLibraryLoading}
        title="Select Looped Variable from Library"
        currentUserEmail={currentUserEmail}
        canPromote={canPromote}
        onSetVisibility={setFrameworkVisibility}
      />
    </Box>
  )
}

export default FrameworkComposer
